import { cn } from "@/lib/utils";

/** A single placeholder block. Text lines are pill-shaped, cards and images are rounded. */
export const Skel = ({ className, round = "md" }) => (
  <div
    aria-hidden="true"
    className={cn(
      "ps-skel",
      { line: "rounded-full", md: "rounded-[14px]", lg: "rounded-[20px]", full: "rounded-full" }[
        round
      ],
      className
    )}
  />
);

/** A paragraph of placeholder lines, last one short so it reads as prose rather than a block. */
export const SkelText = ({ lines = 3, className }) => (
  <div className={cn("space-y-2.5", className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skel
        key={i}
        round="line"
        className={cn("h-3.5", i === lines - 1 ? "w-2/5" : i % 2 ? "w-11/12" : "w-full")}
      />
    ))}
  </div>
);

/** Wraps a loading screen so screen readers announce it instead of reading empty boxes. */
export const SkelScreen = ({ label, testId, children }) => (
  <div role="status" aria-busy="true" aria-label={label} data-testid={testId} className="contents">
    <span className="sr-only">{label}</span>
    {children}
  </div>
);

/** The deep teal hero every plan and chapter opens with. */
const HeroSkel = () => (
  <div className="rounded-[26px] bg-brand-teal/[0.92] px-7 py-9 sm:px-9 sm:py-11">
    <div className="flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1 space-y-4">
        <Skel round="line" className="h-2.5 w-40 bg-white/15" />
        <Skel round="line" className="h-8 w-4/5 bg-white/15" />
        <Skel round="line" className="h-8 w-2/5 bg-white/15" />
        <div className="space-y-2.5 pt-2">
          <Skel round="line" className="h-3 w-full bg-white/10" />
          <Skel round="line" className="h-3 w-3/4 bg-white/10" />
        </div>
        <div className="flex gap-2 pt-2">
          <Skel round="full" className="h-7 w-36 bg-white/12" />
          <Skel round="full" className="h-7 w-44 bg-white/12" />
        </div>
      </div>
      <Skel round="lg" className="aspect-square w-full bg-white/12 sm:w-[240px] sm:shrink-0 lg:w-[280px]" />
    </div>
  </div>
);

/** Plan Overview: hero, then the chapter and progress cards. */
export const ProgramHomeSkeleton = () => (
  <SkelScreen label="Loading your plan" testId="skeleton-program-home">
    <HeroSkel />
    <section className="ps-card ps-card-pad space-y-5">
      <Skel round="line" className="h-4 w-52" />
      <div className="grid gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skel key={i} round="md" className="h-24" />
        ))}
      </div>
    </section>
    <section className="ps-card ps-card-pad space-y-6">
      <div className="flex items-center gap-5">
        <Skel round="full" className="h-16 w-16 shrink-0" />
        <div className="min-w-0 flex-1 space-y-3">
          <Skel round="line" className="h-4 w-2/5" />
          <Skel round="line" className="h-3 w-3/5" />
        </div>
      </div>
      <Skel round="lg" className="h-16" />
    </section>
    <section className="ps-card ps-card-pad space-y-4">
      <Skel round="line" className="h-4 w-44" />
      {[0, 1].map((i) => (
        <Skel key={i} round="md" className="h-20" />
      ))}
    </section>
  </SkelScreen>
);

/** Day view: the day hero, then one card per meal of the day. */
export const DaySkeleton = () => (
  <SkelScreen label="Loading today's meals" testId="skeleton-day">
    <div className="rounded-[26px] bg-brand-teal/[0.92] px-7 py-8 sm:px-9">
      <div className="space-y-4">
        <Skel round="line" className="h-2.5 w-28 bg-white/15" />
        <Skel round="line" className="h-7 w-3/5 bg-white/15" />
        <div className="flex gap-2 pt-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skel key={i} round="full" className="h-9 w-9 bg-white/12" />
          ))}
        </div>
        <Skel round="full" className="mt-2 h-2 w-full bg-white/12" />
      </div>
    </div>
    {[0, 1, 2, 3].map((i) => (
      <article key={i} className="ps-card ps-card-pad">
        <div className="flex gap-5">
          <Skel round="md" className="h-[86px] w-[86px] shrink-0 sm:h-24 sm:w-24" />
          <div className="min-w-0 flex-1 space-y-3">
            <Skel round="line" className="h-2.5 w-24" />
            <Skel round="line" className="h-5 w-3/4" />
            <Skel round="line" className="h-3 w-2/5" />
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <Skel round="full" className="h-11 flex-1" />
          <Skel round="full" className="h-11 flex-1" />
        </div>
      </article>
    ))}
  </SkelScreen>
);

/** Recipe detail, inside the modal: photo, title, meta chips, ingredients, method. */
export const RecipeSkeleton = () => (
  <div role="status" aria-busy="true" aria-label="Loading recipe" data-testid="skeleton-recipe">
    <span className="sr-only">Loading recipe</span>
    <Skel round="md" className="aspect-square w-full rounded-none" />
    <div className="space-y-7 p-7 sm:p-8">
      <div className="space-y-3">
        <Skel round="line" className="h-7 w-3/4" />
        <SkelText lines={2} />
        <div className="flex gap-2 pt-1">
          {[0, 1, 2].map((i) => (
            <Skel key={i} round="full" className="h-8 w-24" />
          ))}
        </div>
      </div>
      <section className="space-y-3">
        <Skel round="line" className="h-2.5 w-24" />
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex justify-between gap-4 py-1">
            <Skel round="line" className="h-3.5 w-1/2" />
            <Skel round="line" className="h-3.5 w-16 shrink-0" />
          </div>
        ))}
      </section>
      <section className="space-y-4">
        <Skel round="line" className="h-2.5 w-20" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-4">
            <Skel round="full" className="h-7 w-7 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skel round="line" className="h-3.5 w-full" />
              <Skel round="line" className="h-3.5 w-4/5" />
            </div>
          </div>
        ))}
      </section>
    </div>
  </div>
);

/** Profile: the account card, then the plans and access cards. */
export const ProfileSkeleton = () => (
  <SkelScreen label="Loading your profile" testId="skeleton-profile">
    <section className="ps-card ps-card-pad space-y-6">
      <div className="flex items-center gap-4">
        <Skel round="full" className="h-14 w-14 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2.5">
          <Skel round="line" className="h-6 w-2/5" />
          <Skel round="line" className="h-3.5 w-3/5" />
        </div>
      </div>
      <Skel round="lg" className="h-20" />
    </section>
    <section className="ps-card ps-card-pad space-y-5">
      <div className="space-y-2.5">
        <Skel round="line" className="h-2.5 w-16" />
        <Skel round="line" className="h-5 w-32" />
        <Skel round="line" className="h-3 w-4/5" />
      </div>
      {[0, 1].map((i) => (
        <Skel key={i} round="lg" className="h-28" />
      ))}
    </section>
    <section className="ps-card ps-card-pad space-y-4">
      <Skel round="line" className="h-4 w-28" />
      <Skel round="full" className="h-12 w-40" />
    </section>
  </SkelScreen>
);

/** My Plans: one placeholder card per plan the reader may own. */
export const LibrarySkeleton = () => (
  <SkelScreen label="Loading your plans" testId="skeleton-library">
    <div className="grid gap-5 sm:grid-cols-2">
      {[0, 1].map((i) => (
        <div key={i} className="ps-card overflow-hidden">
          <Skel round="md" className="h-[190px] rounded-none" />
          <div className="space-y-3.5 p-6">
            <Skel round="line" className="h-2.5 w-24" />
            <Skel round="line" className="h-5 w-4/5" />
            <SkelText lines={2} />
            <Skel round="full" className="mt-2 h-11 w-full" />
          </div>
        </div>
      ))}
    </div>
  </SkelScreen>
);

/** Used while the app is still working out who the reader is, before a route is known. */
export const AppSkeleton = () => (
  <SkelScreen label="Loading your plan" testId="skeleton-app">
    <section className="ps-card ps-card-pad space-y-5">
      <Skel round="line" className="h-2.5 w-28" />
      <Skel round="line" className="h-7 w-3/5" />
      <SkelText lines={3} />
    </section>
    {[0, 1].map((i) => (
      <Skel key={i} round="lg" className="h-32" />
    ))}
  </SkelScreen>
);
